"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { calculateHeight, HeightParams } from "./calculate-height";
import classes from "./dynamic-height.module.css";

interface DynamicHeightContextType {
  setSourceRef: (ref: HeightParams["sourceRef"]) => void;
  setTargetRef: (ref: HeightParams["targetRef"]) => void;
  targetHeight: number | null;
}

const DynamicHeightContext = createContext<DynamicHeightContextType | null>(
  null,
);

interface DynamicHeightProviderProps {
  children: ReactNode;
  bottomSpacing?: number;
}

export const DynamicHeightProvider = ({
  children,
  bottomSpacing = 0,
}: DynamicHeightProviderProps) => {
  const [sourceRef, setSourceRef] = useState<HeightParams["sourceRef"] | null>(
    null,
  );
  const [targetRef, setTargetRef] = useState<HeightParams["targetRef"] | null>(
    null,
  );
  const [targetHeight, setTargetHeight] = useState<number | null>(null);

  useEffect(() => {
    if (sourceRef && targetRef) {
      history.scrollRestoration = "manual";

      const resizeObserver = new ResizeObserver(() => {
        const height = calculateHeight({ sourceRef, targetRef });

        setTargetHeight(height - bottomSpacing);
      });
      resizeObserver.observe(sourceRef);

      setTargetHeight(
        calculateHeight({ sourceRef, targetRef }) - bottomSpacing,
      );

      return () => {
        resizeObserver.disconnect();
        history.scrollRestoration = "auto";
      };
    }
  }, [sourceRef, targetRef, bottomSpacing]);

  return (
    <DynamicHeightContext.Provider
      value={{ setSourceRef, targetHeight, setTargetRef }}
    >
      {children}
    </DynamicHeightContext.Provider>
  );
};

interface DynamicHeightProps {
  children: ReactNode;
}

export const DynamicHeight = ({ children }: DynamicHeightProps) => {
  const context = useContext(DynamicHeightContext);

  if (!context) {
    throw new Error(
      "DynamicHeight must be used within a DynamicHeightProvider",
    );
  }

  const { targetHeight, setTargetRef } = context;

  return (
    <div
      style={{
        height: targetHeight || "100vh",
      }}
      className={classes.root}
      ref={setTargetRef}
    >
      {children}
    </div>
  );
};

interface DynamicHeightSourceProps {
  children: ReactNode;
}

export const DynamicHeightSource = ({ children }: DynamicHeightSourceProps) => {
  const context = useContext(DynamicHeightContext);

  if (!context) {
    throw new Error(
      "DynamicHeightSource must be used within a DynamicHeightProvider",
    );
  }

  const { setSourceRef } = context;

  return <div ref={setSourceRef}>{children}</div>;
};
